import { getBookmarkCount, initBookmarkToggles, renderBookmarksInto, subscribeBookmarks } from './bookmarks';
import { initSearchHighlight } from './search-highlight';
import { initSearchResults, type SearchResultsController } from './search-results';
import { initSpineFooterInset } from './spine';
import { reducedMotion } from './utils';
import { initPanelFades, initVideoPlayer, initVideoTabs } from './video';
import { markWatchedCards } from './watched';

// ─── Overlays (menu + search + bookmarks) ───────────────────────────────────

const desktopMq = window.matchMedia('(min-width: 1150px)');

// Mirrors `--bookmarks-exit-dur` on `.menu--bookmarks` in _bookmarks.scss — how
// long the panel takes to slide out before it's hidden.
const BOOKMARKS_EXIT_MS = 450;
const activeOverlay: {
  closeMenu: (() => void) | null;
  closeSearch: (() => void) | null;
  closeBookmarks: (() => void) | null;
} = {
  closeMenu: null,
  closeSearch: null,
  closeBookmarks: null,
};

const supportsViewTransitions = 'startViewTransition' in document;

// True when a click will trigger a plain same-origin document navigation — the
// case a cross-document view transition crossfades for us. When it returns
// true, an overlay should skip its own close animation and let the browser own
// the visual, so the snapshot captures the overlay fully open (not mid-close).
function navHandledByViewTransition(e: MouseEvent, target: HTMLElement): boolean {
  if (!supportsViewTransitions || reducedMotion()) return false;
  const link = target.closest<HTMLAnchorElement>('a[href]');
  if (!link) return false;
  return (
    e.button === 0 &&
    !e.metaKey &&
    !e.ctrlKey &&
    !e.shiftKey &&
    !e.altKey &&
    !e.defaultPrevented &&
    link.target !== '_blank' &&
    link.origin === window.location.origin
  );
}

// Shared open/close machinery for the simple overlays (menu + search): hidden
// toggling, an isOpen flag, aria sync, Escape-to-close, mutual exclusivity, and
// an optional silent close on breakpoint change. The bookmarks panel has bespoke
// exit choreography and is wired separately below.
interface OverlayOptions {
  toggleSelector: string;
  panelSelector: string;
  bodyClass: string;
  /** aria-labels for the toggle in each state. */
  labels: { open: string; close: string };
  /** Element to focus on open; falls back to the panel itself when null. */
  focusTarget: (panel: HTMLElement) => HTMLElement | null;
  /** Dismiss the other overlays before this one opens. */
  closeOthers: () => void;
  /** Hand the close fn to the coordinator so siblings can call it. */
  registerClose: (close: () => void) => void;
  /** Runs during close, before focus returns to the toggle. */
  onClose?: () => void;
  /** When set, a change here closes the overlay silently (no toggle refocus). */
  closeOnBreakpointChange?: MediaQueryList;
}

interface OverlayHandle {
  panel: HTMLElement;
  open: () => void;
  close: () => void;
  isOpen: () => boolean;
}

function createOverlay(opts: OverlayOptions): OverlayHandle | null {
  const toggleEl = document.querySelector<HTMLButtonElement>(opts.toggleSelector);
  const panelEl = document.querySelector<HTMLElement>(opts.panelSelector);

  if (!toggleEl || !panelEl) return null;

  // Non-null locals so the closures below don't need repeated guards.
  const toggle = toggleEl;
  const panel = panelEl;

  panel.removeAttribute('hidden');

  let isOpen = false;

  function open(): void {
    if (isOpen) return;

    opts.closeOthers();

    panel.classList.add('is-open');
    document.body.classList.add(opts.bodyClass);
    toggle.setAttribute('aria-expanded', 'true');
    toggle.setAttribute('aria-label', opts.labels.close);

    const focusEl = opts.focusTarget(panel);
    if (focusEl) {
      focusEl.focus();
    } else {
      panel.setAttribute('tabindex', '-1');
      panel.focus();
    }

    isOpen = true;
  }

  function close(focusToggle = true): void {
    if (!isOpen) return;

    panel.classList.remove('is-open');
    document.body.classList.remove(opts.bodyClass);
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', opts.labels.open);
    opts.onClose?.();
    if (focusToggle) toggle.focus();

    isOpen = false;
  }

  opts.registerClose(() => close());

  toggle.addEventListener('click', () => {
    if (isOpen) close();
    else open();
  });

  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen) close();
  });

  // Crossing the breakpoint changes which overlay UI applies — drop the panel
  // without yanking focus back to a toggle that may no longer be visible.
  opts.closeOnBreakpointChange?.addEventListener('change', () => close(false));

  return { panel, open, close: () => close(), isOpen: () => isOpen };
}

function initMenu(): void {
  const overlay = createOverlay({
    toggleSelector: '.js-menu-toggle',
    panelSelector: '#main-menu.js-menu',
    bodyClass: 'menu-open',
    labels: { open: 'Open menu', close: 'Close menu' },
    // Focus the panel itself, not the first link: Safari treats a programmatic
    // .focus() as focus-visible, so "Videos" came up ringed as though it were
    // already selected. Landing on the container still puts Tab at the top of
    // the menu. Same reasoning as the bookmarks panel below.
    focusTarget: () => null,
    // Menu shares the spine wipe + icons with the other overlays.
    closeOthers: () => {
      activeOverlay.closeSearch?.();
      activeOverlay.closeBookmarks?.();
    },
    registerClose: (close) => {
      activeOverlay.closeMenu = close;
    },
    closeOnBreakpointChange: desktopMq,
  });

  if (!overlay) return;
  const { panel, close, isOpen } = overlay;

  panel.addEventListener('click', (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!isOpen()) return;

    // The scrim behind the panel dismisses the menu, like clicking outside it.
    if (target.closest('.js-menu-scrim')) {
      close();
      return;
    }

    if (!target.closest('a[href]')) return;
    // Let the cross-document view transition crossfade the navigation instead.
    if (navHandledByViewTransition(e, target)) return;
    close();
  });
}

function initSearch(): void {
  const search = document.querySelector<HTMLElement>('#site-search.js-search');
  const results: SearchResultsController | null = search ? initSearchResults(search) : null;

  createOverlay({
    toggleSelector: '.js-search-toggle',
    panelSelector: '#site-search.js-search',
    bodyClass: 'search-open',
    labels: { open: 'Open search', close: 'Close search' },
    focusTarget: () => document.querySelector<HTMLElement>('#site-search-input'),
    closeOthers: () => {
      activeOverlay.closeMenu?.();
      activeOverlay.closeBookmarks?.();
    },
    registerClose: (close) => {
      activeOverlay.closeSearch = close;
    },
    onClose: () => results?.reset(),
  });
}

function initBookmarks(): void {
  const toggleEl = document.querySelector<HTMLButtonElement>('.js-bookmarks-toggle');
  const menuEl = document.querySelector<HTMLElement>('#bookmarks-menu.js-bookmarks');
  // Both the base spine layer and the parchment wipe copy carry a badge, so the
  // count survives when the wipe slides over the base on menu/search open.
  const countEls = document.querySelectorAll<HTMLElement>('.js-bookmarks-count');

  // The count badge reflects state even where the overlay isn't present, as
  // does the icon's fill — the bookmark stays an empty outline until something
  // is actually saved (see `body.has-bookmarks` in _spine.scss).
  const syncCount = (): void => {
    const count = getBookmarkCount();
    countEls.forEach((el) => {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
    document.body.classList.toggle('has-bookmarks', count > 0);
  };
  syncCount();
  subscribeBookmarks(syncCount);

  if (!toggleEl || !menuEl) return;

  // Non-null locals so the closures below don't need repeated guards.
  const toggle = toggleEl;
  const menu = menuEl;

  menu.removeAttribute('hidden');

  const listEl = menu.querySelector<HTMLElement>('.js-bookmarks-list');
  const emptyEl = menu.querySelector<HTMLElement>('.js-bookmarks-empty');

  const render = (): void => {
    if (!listEl) return;
    renderBookmarksInto(listEl, emptyEl);
    // Freshly injected cards still need the "watched" treatment applied.
    markWatchedCards(listEl);
  };

  // Re-render live while open so removals from within the menu reflow it.
  let isOpen = false;
  let isClosing = false;
  let closeTimer: ReturnType<typeof window.setTimeout> | null = null;

  subscribeBookmarks(() => {
    if (isOpen) render();
  });

  function clearCloseTimer(): void {
    if (closeTimer !== null) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }
  }

  // Instantly drops any in-flight exit state with no animation — used when
  // another overlay needs to take over (mutual exclusivity), the breakpoint
  // changes, or reduced motion is preferred. Forces `visibility: hidden`
  // immediately via inline style: `.menu`'s visibility transition is timed
  // off the original close (see `--menu-exit-dur`), so without this, cards
  // would otherwise revert to their fully-visible base styles (no animation
  // class applies once `.is-open`/`.is-closing` are both gone) and flash
  // on top of the page for the remainder of that window.
  function hardClose(): void {
    clearCloseTimer();
    isClosing = false;
    menu.classList.remove('is-open', 'is-closing');
    document.body.classList.remove('bookmarks-open');
    menu.style.visibility = 'hidden';
  }

  function open(): void {
    if (isOpen) return;

    activeOverlay.closeMenu?.();
    activeOverlay.closeSearch?.();

    // Cancel any in-flight close so the entrance starts from a clean state,
    // and clear any inline visibility override from a prior hardClose().
    if (isClosing) hardClose();
    menu.style.visibility = '';

    render();
    menu.classList.add('is-open');
    document.body.classList.add('bookmarks-open');
    toggle.setAttribute('aria-expanded', 'true');
    toggle.setAttribute('aria-label', 'Close bookmarks');

    // Focus the overlay container itself, not the first card link — focusing a
    // card would trigger its `:focus-within` styling and read as a hover on the
    // top bookmark the moment the menu opens.
    menu.setAttribute('tabindex', '-1');
    menu.focus();

    isOpen = true;
  }

  function close(): void {
    if (!isOpen) return;

    isOpen = false;
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Open bookmarks');
    toggle.focus();

    // Release the page scroll lock immediately on click (`body.bookmarks-open`
    // sets `overflow: hidden`); it's independent of the overlay's internal
    // card-exit choreography, which finishes on the exit timer below.
    document.body.classList.remove('bookmarks-open');

    if (reducedMotion()) {
      hardClose();
      return;
    }

    // Removing `.is-open` slides the sheet back out and fades the scrim; the
    // root stays rendered for `--bookmarks-exit-dur` via its delayed
    // `visibility` transition. `.is-closing` marks the exit as in flight and is
    // cleared once the slide finishes — see `--bookmarks-exit-dur` in
    // _bookmarks.scss.
    menu.classList.remove('is-open');
    menu.classList.add('is-closing');
    isClosing = true;

    clearCloseTimer();
    closeTimer = window.setTimeout(() => {
      closeTimer = null;
      isClosing = false;
      menu.classList.remove('is-closing');
    }, BOOKMARKS_EXIT_MS);
  }

  // Forces an immediate close with no exit animation — for the mutual
  // exclusivity coordinator, where bookmarks must be out of the way before
  // another overlay (sharing the same z-index band) animates in.
  function closeImmediate(): void {
    if (!isOpen && !isClosing) return;

    isOpen = false;
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Open bookmarks');
    hardClose();
  }

  activeOverlay.closeBookmarks = closeImmediate;

  toggle.addEventListener('click', () => {
    if (isOpen) close();
    else open();
  });

  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen) close();
  });

  menu.addEventListener('click', (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!isOpen) return;

    // Removing a bookmark never dismisses the panel. This has to come first:
    // the remove button's own handler re-renders the list, so by the time the
    // click bubbles up here the target is detached and every ancestor lookup
    // below (`.bookmarks-panel`) misses, reading as a click on the scrim.
    if (target.closest('.js-bookmark-remove')) return;

    // The panel's own X control dismisses it.
    if (target.closest('.js-bookmarks-close')) {
      close();
      return;
    }

    // Clicking the scrim (anywhere outside the sheet) dismisses the panel.
    if (!target.closest('.bookmarks-panel')) {
      close();
      return;
    }

    // Close when following a card link.
    if (!target.closest('a[href]')) return;
    // Let the cross-document view transition crossfade the navigation instead.
    if (navHandledByViewTransition(e, target)) return;
    close();
  });

  desktopMq.addEventListener('change', () => {
    if (isOpen) closeImmediate();
  });
}

// ─── Bootstrap ────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  initMenu();
  initSearch();
  initVideoTabs();
  initPanelFades();
  initSearchHighlight();
  void initVideoPlayer();
  markWatchedCards();
  initBookmarkToggles();
  initBookmarks();
  initSpineFooterInset();
});
